/**
 * NotificationCell — Single notification row (icon + text only)
 *
 * Layout: [Avatar] [message + timeAgo] [chevron for friend_request]
 * Tapping emits 'tap' — parent decides what to do (open detail view, etc.)
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import type { GameNotification } from '@sdl/shared';

interface NotificationCellEvents {
  'tap': { notification: GameNotification };
}

export class NotificationCell extends EventEmitter<NotificationCellEvents> {
  public element: HTMLElement;
  private data: GameNotification;

  constructor(data: GameNotification) {
    super();
    this.data = data;
    this.element = this.createElement();
  }

  getData(): GameNotification {
    return this.data;
  }

  animateRemoval(): Promise<void> {
    return new Promise((resolve) => {
      this.element.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
      this.element.style.transform = 'translateX(100%)';
      this.element.style.opacity = '0';
      setTimeout(() => {
        this.element.remove();
        resolve();
      }, 300);
    });
  }

  destroy(): void {
    this.element.remove();
    this.removeAllListeners();
  }

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = `notif-cell${this.data.seen ? '' : ' notif-unseen'}`;
    el.addEventListener('click', () => {
      this.emit('tap', { notification: this.data });
    });
    this.render(el);
    return el;
  }

  private render(el: HTMLElement): void {
    const { fromUsername, fromPicture, createdAt } = this.data;
    const initial = fromUsername.charAt(0).toUpperCase();
    const avatarContent = fromPicture
      ? `<img src="${fromPicture}" alt="${initial}" class="notif-avatar-img">`
      : `<span class="notif-avatar-initial">${initial}</span>`;

    const timeStr = this.timeAgo(createdAt);
    let message = '';
    let chevron = '';

    if (this.data.type === 'friend_request') {
      message = `<strong>${fromUsername}</strong> wants to be your friend`;
      chevron = `<div class="notif-chevron">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" stroke-width="2">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </div>`;
    } else if (this.data.type === 'gift') {
      const amount = this.data.data.amount;
      message = `<strong>${fromUsername}</strong> sent you <span class="notif-stacks">$${amount.toLocaleString()}</span> stacks`;
    }

    el.innerHTML = `
      <div class="notif-avatar">${avatarContent}</div>
      <div class="notif-body">
        <div class="notif-message">${message}</div>
        <div class="notif-time">${timeStr}</div>
      </div>
      ${chevron}
    `;
  }

  private timeAgo(timestamp: number): string {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  }
}
